import { useEffect, useMemo, useState } from 'react';

import BoardContextProvider from 'lib/context/BoardContext/BoardContextProvider';
import { useDefaultFilters } from 'lib/hooks/queries/filters/useDefaultFilters';
import { useBillingOverview } from 'lib/hooks/queries/widgets/useBilling';

import ClaimsQueue from 'components/practice/BeyondBilling/Overview/widgets/ClaimsQueue';
import ERAEOB from 'components/practice/BeyondBilling/Overview/widgets/ERA&EOB';
import GrandTotal from 'components/practice/BeyondBilling/Overview/widgets/GrandTotal';
import Invoices from 'components/practice/BeyondBilling/Overview/widgets/Invoices';
import Superbills from 'components/practice/BeyondBilling/Overview/widgets/Superbills';
import ViewTabs from 'components/shared/AgTablePage/components/ViewTabs';
import { createQueryFilters } from 'components/shared/AgTablePage/lib/createQueryFilters';
import Board from 'components/shared/CustomizableBoard/Board';
import HeaderButton from 'components/shared/CustomizableBoard/HeaderButton';
import { applyFilterHandler } from 'components/shared/Filter/lib/helpers/handleAppliedFilter';
import Filters from 'components/shared/Filters/Filters';
import FilterActions from 'components/shared/Filters/components/FilterActions';
import Header from 'components/shared/Header/Header';

import { defaultFilters } from './lib/helpers';

const name = 'beyond_billing_overview';

const widgets = {
  grand_total: GrandTotal,
  billing_claims: ClaimsQueue,
  billing_era_eob: ERAEOB,
  billing_invoices: Invoices,
  billing_superbills: Superbills
};

export default function BeyondBillingOverview() {
  const { data: savedFilters = {}, isFetched } = useDefaultFilters({});
  const [isEditingFilter, setIsEditingFilter] = useState(false);

  const [initialFilters, setInitialFilters] = useState(
    applyFilterHandler(defaultFilters, savedFilters[name] || null)
  );
  const [filters, setFilters] = useState(
    applyFilterHandler(defaultFilters, savedFilters[name] || null)
  );

  useEffect(() => {
    if (isFetched) {
      setFilters(applyFilterHandler(defaultFilters, savedFilters[name] || null));
      setInitialFilters(applyFilterHandler(defaultFilters, savedFilters[name] || null));
    }
  }, [isFetched]);

  const newFilters = useMemo(() => createQueryFilters(filters), [filters]);

  const { data } = useBillingOverview({
    params: { filters: newFilters },
    dependencies: [filters]
  });

  return (
    <BoardContextProvider board="beyond_billing_overview" cols={{ lg: 4, md: 2, sm: 2 }}>
      <Board
        wrapperClassName="overflow-auto"
        rootClassName="h-full"
        widgets={widgets}
        overview={data}
        showInfoInRecordings={true}>
        <HeaderButton
          header={() => (
            <Header title="Reporting Overview" className="w-full !border-b-0">
              <ViewTabs
                category="beyond_billing_overview"
                title="Reporting Overview"
                filters={filters}
                setFilters={setFilters}
                defaultFilters={defaultFilters}
                setInitialFilters={setInitialFilters}
                isEditingFilter={isEditingFilter}
                setIsEditingFilter={setIsEditingFilter}
              />
            </Header>
          )}
        />
        <div className="outline-b flex items-center justify-between gap-2 bg-white px-4 py-2 outline-1 outline-offset-[-1px] outline-neutral-200">
          <Filters defaultFilters={defaultFilters} filters={filters} setFilters={setFilters} />
          <div className="flex h-full flex-wrap items-start gap-2">
            <FilterActions
              filters={filters}
              initialFilters={initialFilters}
              setFilters={setFilters}
              category="beyond_billing_overview"
              isEditingFilter={isEditingFilter}
            />
          </div>
        </div>
      </Board>
    </BoardContextProvider>
  );
}
